import { LiveValue, type ConnectionState, type HyloServices, type Update, type RealtimeSignal } from '@/model';
import { audioFocusFor } from './audio-focus';
import { createHttpClient } from './http';
import { createPrefs } from './prefs';
import { SessionProtocol } from './protocol';
import { SessionController } from './session';
import { SyncEngine } from './sync';
import { Queries } from './queries';
import { createMediaFacade, createMediaService } from './media';
import { Outbox } from './outbox';
import { createResources } from './resources';
import { createMessages } from './messages';
import { createFlags } from './flags';
import { createTyping } from './signals';
import { admissionFor } from './capabilities';
import { DocumentJournal } from './document-journal';
import { attachCoreDataHost } from './document-host';
import type { AccountStore } from './store';

interface Runtime { store: AccountStore; journal: DocumentJournal; sync: SyncEngine; outbox: Outbox; media: ReturnType<typeof createMediaService>; unsubscribe(): void }
export function createRealServices(options: { baseUrl?: string } = {}): HyloServices {
  const protocol = new SessionProtocol();
  const queries = new Queries();
  const mediaFacade = createMediaFacade();
  const connection = new LiveValue<ConnectionState>({ phase: 'idle', since: Date.now() });
  const updateListeners = new Set<{ types: readonly string[] | '*'; handler: (u: Update) => void }>();
  const hasSnapshot = new LiveValue(false);
  const signalListeners = new Set<{ types: readonly string[] | '*'; handler: (signal: RealtimeSignal) => void }>();
  let runtime: Runtime | undefined;
  let lifecycle = 0;
  const stop = async () => {
    lifecycle++;
    admissionFor(http)?.reset();
    const old = runtime; runtime = undefined;
    globalThis.removeEventListener?.('focus', focus);
    globalThis.document?.removeEventListener('visibilitychange', visible);
    queries.setStore(); mediaFacade.set(); messages.stop(); resources.drafts.stop(); typing.reset(); flags.reset();
    old?.unsubscribe(); old?.sync.stop(); old?.media.dispose();
    await old?.journal.retire();
    hasSnapshot.set(false);
    connection.set({ phase: 'idle', since: Date.now() });
    if (old) await old.outbox.stop();
  };
  const session = new SessionController({ baseUrl: options.baseUrl, onStop: stop, onStart: async (_registration, db) => {
    audioFocusFor(services);
    const current = ++lifecycle;
    const media = createMediaService(db, http); const outbox = new Outbox(db, http, media);
    const sync = new SyncEngine({ store: db, http, deviceId: session.deviceId, accessToken: () => session.accessToken(), refresh: () => session.refresh(), protocol: () => protocol.version, baseUrl: options.baseUrl,
      onReady: () => { messages.wake(); void refreshFlags(); void session.refreshDevice().catch(() => undefined); },
      onSignal: signal => { if (signal.type === 'typing') typing.receive(signal); else if (signal.type === 'flags' || signal.type === 'trace') void refreshFlags(); },
    });
    const connectionUnsubscribe = sync.connection.subscribe(() => {
      if (runtime?.sync !== sync) return;
      const next = sync.connection.get(); connection.set(next); outbox.setPaused(next.phase === 'resyncing' || next.phase === 'syncing'); messages.setPaused(next.phase === 'resyncing' || next.phase === 'syncing'); resources.drafts.setPaused(next.phase === 'resyncing' || next.phase === 'syncing');
      if (next.phase === 'connecting') messages.refreshWindows();
      if (next.phase !== 'online') typing.reset();
    });
    const snapshotUnsubscribe = sync.hasSnapshot.subscribe(() => { if (runtime?.sync === sync) hasSnapshot.set(sync.hasSnapshot.get()); });
    const signalUnsubscribe = sync.onSignal('*', signal => { for (const listener of signalListeners) if (listener.types === '*' || listener.types.includes(signal.type)) { try { listener.handler(signal); } catch { /* Isolate feature consumers. */ } } });
    const updateUnsubscribe = sync.onUpdate('*', update => {
      for (const listener of updateListeners) if (listener.types === '*' || listener.types.includes(update.type)) { try { listener.handler(update); } catch { /* A feature cannot interrupt sync. */ } }
    });
    runtime = { store: db, journal: new DocumentJournal(db), sync, outbox, media, unsubscribe: () => { connectionUnsubscribe(); updateUnsubscribe(); snapshotUnsubscribe(); signalUnsubscribe(); } };
    queries.setStore(db); mediaFacade.set(media);
    await flags.refresh();
    if (current !== lifecycle || runtime?.store !== db) return;
    await sync.start();
    if (current !== lifecycle || runtime?.store !== db) return;
    outbox.start(); messages.start(); resources.drafts.start();
    globalThis.addEventListener?.('focus', focus); globalThis.document?.addEventListener('visibilitychange', visible);
  } });
  const http = createHttpClient({ baseUrl: options.baseUrl, auth: session, protocol: () => protocol.version }); session.setHttp(http);
  admissionFor(http)?.state.subscribe(() => { if (session.state.get().phase === 'signedIn' && admissionFor(http)?.state.get().includes('support.v1')) void session.refreshDevice().catch(() => undefined); });
  const deps = { http, queries, session, store: () => runtime?.store };
  const resources = createResources(deps);
  const messages = createMessages({ ...deps, outbox: () => runtime?.outbox });
  const flags = createFlags(http, protocol);
  const typing = createTyping((id, state) => runtime?.sync.sendTyping(id, state), () => runtime?.store.accountId);
  const refreshFlags = async () => { const active = runtime; if (!active) return; const changed = await flags.refresh(); if (active === runtime && changed) await active.sync.resync().catch(() => undefined); };
  const focus = () => { if (!runtime) return; void refreshFlags(); };
  const visible = () => { if (document.visibilityState === 'visible') focus(); };
  const services: HyloServices = {
    http, session, ...resources, messages, flags, typing,
    prefs: createPrefs((() => { try { return globalThis.localStorage; } catch { return undefined; } })(), { server: options.baseUrl, account: session.lifetime }),
    sync: { connection, hasSnapshot, reconnect: () => runtime?.sync.reconnect(), onSignal(types, handler) { const entry = { types, handler }; signalListeners.add(entry); return () => { signalListeners.delete(entry); }; }, onUpdate(types, handler) { const listener = { types, handler }; updateListeners.add(listener); return () => { updateListeners.delete(listener); }; }, resync: () => runtime?.sync.resync() ?? Promise.resolve() },
    media: mediaFacade,
  };
  attachCoreDataHost(services, { store: () => runtime?.store, journal: () => runtime?.journal });
  queueMicrotask(() => { void session.restore(); });
  return services;
}
